import React from 'react';
import {useFlightWorker} from '../../../app/hooks/use-flight-worker';
import {useAppSelector} from '../../../app/hooks/store-hooks';
import {AirportTooltipDto} from '../../../airports/airport-dto';
import {AirportSelection} from '../../../common/enums';
import {MessageType} from '../../../services/actor-messages';
import {AirportTooltipData} from '../../../services/map-interaction-service';
import {FeNavAirport} from '../../../static/nav-types';
import {
    airportInfoFields,
    airportSelectionFields,
    AirportTooltipFieldSelection,
    formatAirportInfoField
} from '../../../static/static-airport-tooltip-fields';
import {getFormattedAirportField} from '../../../app/features/airport-list/AirportListCells';

export function AirportTooltip(props: { info: AirportTooltipData }) {
    const context = props.info;
    const airport = context.airport;
    const fields = useAppSelector((x) => x.settings.airportTooltipFields);
    const worker = useFlightWorker();

    const [tooltipData, setTooltipData] = React.useState<AirportTooltipDto | null>(null);

    const id = airport.icaoid || airport.iataid || airport.faaid;

    React.useEffect(() => {
        if (!id) {
            setTooltipData(null);
            return;
        }
        worker.actor.sendAsync({type: MessageType.getAirportTooltipData, data: id}).then((info) => {
            setTooltipData(info);
        });
    }, [worker, id]);

    return (
        <div
            style={{
                width: 150,
                fontSize: 11,
                backgroundColor: 'var(--bg-color)',
                padding: '0.5em'
            }}>
            <div style={{fontSize: '1.25em', fontWeight: 500}} className="text-center">
                <div>{formatId(airport)}</div>
                <div className="secondary-text-color overflow-hidden" style={{fontSize: 10, textOverflow: 'ellipsis'}}>
                    {airport.name}
                </div>
            </div>

            {fields.length > 0 && (
                <div
                    className="border-top pt-1 mt-1"
                    style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gridColumnGap: '0.25em'}}>
                    {fields.map((field, i) => (
                        <AirportTooltipField key={i} navAirport={airport} tooltipData={tooltipData} field={field}/>
                    ))}
                </div>
            )}
        </div>
    );
}

function AirportTooltipField({
                                 navAirport,
                                 tooltipData,
                                 field
                             }: {
    navAirport: FeNavAirport;
    tooltipData: AirportTooltipDto | null;
    field: AirportTooltipFieldSelection;
}) {
    if (typeof field === 'number') {
        if (field === AirportSelection.None) {
            return null;
        }
        if (!tooltipData) {
            return (<>
                <div className="secondary-text-color">{airportSelectionFields[field]?.name ?? ''}</div>
                <div>-</div>
            </>)
        }

        const value = getFormattedAirportField(tooltipData, field);

        return (
            <>
                <div className="secondary-text-color">{airportSelectionFields[field]?.name ?? ''}</div>
                <div>{value ?? '-'}</div>
            </>
        );
    }

    const value = formatAirportInfoField(navAirport, field);

    return (
        <>
            <div className="secondary-text-color">{airportInfoFields[field]?.name ?? ''}</div>
            <div>{value ?? '-'}</div>
        </>
    );
}

function formatId(airport: { iataid: string | null; icaoid: string | null; faaid: string | null }) {
    const ids = [airport.iataid, airport.icaoid];
    if (airport.faaid && airport.faaid !== airport.iataid) {
        ids.push(airport.faaid);
    }
    return ids.join('/');
}
